import type { ReactNode } from "react";
import { ChevronRight, Loader2, Pencil } from "lucide-react";

import { getCriterionIcon } from "../../criterionCatalog";
import {
  formatColumnAggregateResult,
  formatScorecardScore,
  getCriterionMetadataLabel,
  getCriterionPreviewMetadata,
  getScoreProgressTone,
  getVerdictBadgeClassName,
  getVerdictProgressTone,
  hasComputedCriterionSummary,
  isColumnAggregatePrimitive,
  type ScorecardColumnReference,
  type ScorecardProgressTone,
} from "../../utils";
import { SCORECARD_EVALUATOR_CARD, SCORECARD_ROW_DIVIDER } from "../../constants";
import ScorecardRerunIconButton from "../../ScorecardRerunIconButton";
import ScorecardScoreDeltaBadge from "../Detail/ScorecardScoreDeltaBadge";
import ScorecardAssertionResults from "../ScorecardAssertionResults";
import SmartTableStalenessIndicator from "../../../../../SmartTableStalenessIndicator";

import ScorecardCriterionProgressBar from "./ScorecardCriterionProgressBar";

import { BasicTooltip } from "@/components/ui/Tooltip";
import {
  OverflowDirection,
  OverflowDisplayTooltip,
} from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import {
  ScorecardPrimitiveType,
  type CriterionSummary,
  type ScorecardAssertionSummary,
  type ScorecardStep,
} from "@/queries/smart-tables/scorecard";

const renderAggregateValueTooltip = (value: string) => (
  <div className="max-h-80 max-w-[520px] overflow-y-auto whitespace-pre-wrap break-words text-left text-xs leading-5">
    {value}
  </div>
);

const ScorecardMetadataLine = ({ value }: { value: string }) => (
  <OverflowDisplayTooltip
    content={value}
    overflowDirection={OverflowDirection.VERTICAL}
    lineClamp={2}
    side="left"
    className="w-full break-words text-xs leading-5 text-muted-foreground"
    styles={{ wordBreak: "break-word" }}
  >
    <span>{value}</span>
  </OverflowDisplayTooltip>
);

interface ScorecardCriterionRowProps {
  step?: ScorecardStep;
  summary?: CriterionSummary;
  isRemoved?: boolean;
  primitiveType?: string;
  titleOverride?: string;
  columns?: ScorecardColumnReference[];
  onOpen?: () => void;
  onEdit?: () => void;
  onRerun?: () => void;
  isRerunning?: boolean;
  isRerunDisabled?: boolean;
  isStale?: boolean;
  displayMode?: "list" | "card";
  assertionSummaries?: ScorecardAssertionSummary[];
}

const ScorecardCriterionRow = ({
  step,
  summary,
  isRemoved = false,
  primitiveType,
  titleOverride,
  columns = [],
  onOpen,
  onEdit,
  onRerun,
  isRerunning = false,
  isRerunDisabled = false,
  isStale = false,
  displayMode = "list",
  assertionSummaries: assertionSummariesOverride,
}: ScorecardCriterionRowProps) => {
  const title = titleOverride ?? summary?.title ?? step?.title ?? "Evaluator";
  const resolvedPrimitiveType = primitiveType ?? step?.primitive_type ?? "";
  const hasAggregateResult = Boolean(summary?.aggregate_result);
  const isColumnAggregate = isColumnAggregatePrimitive(resolvedPrimitiveType) || hasAggregateResult;
  const Icon = getCriterionIcon(
    isColumnAggregate ? ScorecardPrimitiveType.COLUMN_AGGREGATE : resolvedPrimitiveType,
  );
  const score = summary?.score ?? null;
  const metadataLabel = getCriterionMetadataLabel(step, columns, {
    isColumnAggregate,
  });
  const columnAggregateDisplay = isColumnAggregate
    ? formatColumnAggregateResult(
        summary?.aggregate_result,
        step?.primitive_config?.aggregation_type as string | undefined,
      )
    : null;
  const isScoredColumnAggregate = Boolean(columnAggregateDisplay?.contributesToScore);
  const shouldShowScoreBar = !isColumnAggregate || isScoredColumnAggregate;
  const hasDelta =
    typeof summary?.drift === "number" && Math.round(Math.abs(summary.drift) * 100) > 0;
  const hasComputedResult = hasComputedCriterionSummary(summary);
  const effectiveIsStale = isStale && !isRerunning;
  const isPendingRun = !isRemoved && isRerunning && !hasComputedResult;
  const isNotRun = !isRemoved && !isPendingRun && !hasComputedResult;
  const displayedValue = columnAggregateDisplay?.primary ?? formatScorecardScore(score);
  const hasDisplayValue = Boolean(
    columnAggregateDisplay || (!hasDelta && typeof score === "number"),
  );
  const hasAggregateDisplayValue = Boolean(columnAggregateDisplay || typeof score === "number");
  let tone: ScorecardProgressTone = getScoreProgressTone(score, step?.thresholds);
  if (summary?.verdict) {
    tone = getVerdictProgressTone(summary.verdict);
  }
  if (effectiveIsStale) {
    tone = "stale";
  }
  const assertionSummaries =
    resolvedPrimitiveType === ScorecardPrimitiveType.LLM_ASSERTION
      ? (assertionSummariesOverride ?? summary?.assertion_summaries ?? [])
      : [];
  const previewMetadata = Array.from(
    new Set(
      assertionSummaries.length === 0 && step ? getCriterionPreviewMetadata(step, columns) : [],
    ),
  );
  const metadataItems = Array.from(
    new Set([metadataLabel, ...previewMetadata].filter((item): item is string => Boolean(item))),
  );
  const showRerunAction = Boolean(onRerun && (hasComputedResult || effectiveIsStale || isNotRun));
  const aggregateResultNode =
    isColumnAggregate && hasAggregateDisplayValue ? (
      <div className="min-w-0 rounded-md bg-muted/25 px-2.5 py-2">
        <div className="flex min-w-0 items-start gap-2">
          <div className="min-w-0 flex-1">
            <OverflowDisplayTooltip
              content={renderAggregateValueTooltip(displayedValue)}
              overflowDirection={OverflowDirection.VERTICAL}
              lineClamp={3}
              side="left"
              className="w-full break-words text-left text-base font-semibold leading-5 tracking-tight text-foreground"
              styles={{ wordBreak: "break-word" }}
            >
              <span>{displayedValue}</span>
            </OverflowDisplayTooltip>
          </div>
          {hasDelta ? <ScorecardScoreDeltaBadge drift={summary?.drift} /> : null}
        </div>
        {columnAggregateDisplay?.secondary ? (
          <p className="mt-0.5 break-words text-[11px] leading-4 text-muted-foreground">
            {columnAggregateDisplay.secondary}
          </p>
        ) : null}
      </div>
    ) : null;
  let valueDisplayNode: ReactNode = null;
  if (!isColumnAggregate && hasDisplayValue) {
    valueDisplayNode =
      displayMode === "card" ? (
        <span
          className={cn(
            "inline-flex h-5 items-center rounded-md border px-1.5 text-[11px] font-semibold tabular-nums",
            getVerdictBadgeClassName(summary?.verdict),
          )}
        >
          {displayedValue}
        </span>
      ) : (
        <span className="text-sm font-semibold tabular-nums text-foreground">{displayedValue}</span>
      );
  }

  const containerClassName =
    displayMode === "card" ? SCORECARD_EVALUATOR_CARD : SCORECARD_ROW_DIVIDER;
  const iconNode = (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md text-muted-foreground ring-1",
        displayMode === "card"
          ? "h-7 w-7 bg-muted/35 ring-border/40"
          : "mt-0.5 h-6 w-6 bg-background shadow-sm ring-border/70",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </div>
  );
  const titleNode = (
    <div className="min-w-0 flex-1 space-y-1">
      <span className="flex min-w-0 items-center gap-1.5">
        <span
          className={cn(
            "truncate text-sm leading-5 text-foreground",
            displayMode === "card" ? "font-semibold" : "font-medium",
          )}
        >
          {title}
        </span>
        {!isRemoved && effectiveIsStale ? (
          <SmartTableStalenessIndicator
            className="shrink-0"
            tooltipContent="Stale — source data or config changed. Re-run to refresh."
          />
        ) : null}
      </span>
      {isRemoved || isPendingRun || isNotRun ? (
        <div className="flex min-w-0 flex-wrap gap-x-2 gap-y-0.5">
          {isRemoved ? (
            <span className="whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Removed
            </span>
          ) : null}
          {isPendingRun ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-blue-700">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Running
            </span>
          ) : null}
          {isNotRun ? (
            <span className="whitespace-nowrap text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Not run
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
  const actionsNode = (
    <div className="flex shrink-0 items-start gap-1">
      {valueDisplayNode}
      {!isColumnAggregate && hasDelta ? <ScorecardScoreDeltaBadge drift={summary?.drift} /> : null}
      {onEdit ? (
        <BasicTooltip content={`Edit ${title}`} side="top" delayDuration={300}>
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              onEdit();
            }}
            aria-label={`Edit ${title}`}
            className="rounded p-1 text-muted-foreground opacity-55 transition-[background-color,color,opacity] hover:bg-muted hover:text-foreground hover:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </BasicTooltip>
      ) : null}
      {showRerunAction ? (
        <ScorecardRerunIconButton
          label={title}
          onClick={event => {
            event.stopPropagation();
            onRerun?.();
          }}
          isRerunning={isRerunning}
          isDisabled={isRerunDisabled}
          tooltipContent={isNotRun ? "Run evaluator" : undefined}
          ariaLabel={isNotRun ? `Run ${title}` : undefined}
        />
      ) : null}
      {!isRemoved && onOpen ? (
        <ChevronRight
          className={cn(
            "h-4 w-4 text-muted-foreground transition-opacity",
            displayMode === "card" && "opacity-30 group-hover:opacity-100",
          )}
        />
      ) : null}
    </div>
  );
  const detailsNode = (
    <>
      {aggregateResultNode}
      {metadataItems.length > 0 ? (
        <div className="min-w-0 space-y-0.5">
          {metadataItems.map(item => (
            <ScorecardMetadataLine key={item} value={item} />
          ))}
        </div>
      ) : null}
      <ScorecardAssertionResults assertions={assertionSummaries} />
    </>
  );
  const progressNode =
    shouldShowScoreBar && !isNotRun ? (
      <ScorecardCriterionProgressBar
        score={score}
        tone={isRemoved ? "neutral" : tone}
        className="h-1"
      />
    ) : null;
  const rowContent =
    displayMode === "card" ? (
      <div className="flex h-full w-full flex-col px-3 py-3 text-left">
        <div className="flex items-start gap-2.5">
          {iconNode}
          <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
            {titleNode}
            {actionsNode}
          </div>
        </div>
        <div className="mt-2 min-w-0 flex-1 space-y-1.5">{detailsNode}</div>
        {progressNode ? <div className="mt-2">{progressNode}</div> : null}
      </div>
    ) : (
      <div className="flex h-full w-full items-start gap-3 px-3 py-3 text-left">
        {iconNode}
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-3">
            {titleNode}
            {actionsNode}
          </div>
          {detailsNode}
          {progressNode}
        </div>
      </div>
    );

  if (isRemoved) {
    return (
      <div className={containerClassName}>
        <BasicTooltip
          content="This evaluator was removed. Scores shown are from the last calculation — re-run to update."
          side="left"
        >
          <div className="opacity-50">{rowContent}</div>
        </BasicTooltip>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {onOpen ? (
        <div
          role="button"
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onOpen();
            }
          }}
          className="group block h-full w-full cursor-pointer transition-colors hover:bg-muted/20 focus:outline-none focus-visible:bg-muted/20"
        >
          {rowContent}
        </div>
      ) : (
        rowContent
      )}
    </div>
  );
};

export default ScorecardCriterionRow;
